/**
 * Componentes de identidade visual do envpulse.
 *
 *  - <BrandSymbol />   ícone-só (hexágono + linha de ECG). Reutilizável em qualquer tamanho.
 *  - <BrandWordmark /> texto "env" bold + "pulse" light ciano.
 *  - <BrandLockup />   símbolo + wordmark juntos (ex: header de login, sidebar).
 *
 * O símbolo carrega a metáfora central: o "pulso" (ECG) dentro de um
 * hexágono que representa o ambiente monitorado.
 */

import clsx from "clsx";

interface SymbolProps {
  className?: string;
  /** Aplica drop-shadow cyan glow ao redor do símbolo. */
  glow?: boolean;
}

export function BrandSymbol({ className, glow }: SymbolProps) {
  return (
    <svg
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      className={clsx(className, glow && "drop-shadow-[0_0_18px_rgba(34,211,238,0.45)]")}
      role="img"
      aria-label="envpulse"
    >
      {/* Hexágono flat-top, outline ciano */}
      <polygon
        points="180,100 140,30.7 60,30.7 20,100 60,169.3 140,169.3"
        fill="none"
        stroke="#22D3EE"
        strokeWidth="2.5"
        opacity="0.95"
      />

      {/* Linha de ECG/batimento */}
      <polyline
        points="35,100 70,100 78,108 88,55 96,140 104,80 112,100 165,100"
        fill="none"
        stroke="#E8EEF7"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Ponto luminoso no pico R */}
      <circle cx="88" cy="55" r="3" fill="#FFFFFF" />
    </svg>
  );
}

interface WordmarkProps {
  className?: string;
  /**
   * Variante de cor:
   *   "auto"  → 'env' usa text-slate-900 dark:text-slate-100, 'pulse' ciano (default; tema claro)
   *   "light" → 'env' branco, 'pulse' ciano (pra fundo escuro)
   */
  variant?: "auto" | "light";
}

export function BrandWordmark({ className, variant = "auto" }: WordmarkProps) {
  return (
    <span
      className={clsx(
        "font-sans font-bold tracking-tight leading-none select-none",
        className,
      )}
    >
      <span className={variant === "light" ? "text-white" : "text-slate-900 dark:text-slate-100"}>
        env
      </span>
      <span className="font-light text-[#22D3EE]">
        pulse
      </span>
    </span>
  );
}

interface LockupProps {
  /** Tailwind size pro símbolo (ex: "h-9 w-9"). Wordmark escala junto. */
  size?: "sm" | "md" | "lg";
  variant?: "auto" | "light";
  /** Mostra a tagline "MONITORAMENTO INTELIGENTE" abaixo. */
  withTagline?: boolean;
  className?: string;
  glow?: boolean;
}

const SIZE_MAP = {
  sm: { sym: "h-8 w-8",   wm: "text-base"   },
  md: { sym: "h-10 w-10", wm: "text-xl"     },
  lg: { sym: "h-16 w-16", wm: "text-3xl"    },
} as const;

export function BrandLockup({
  size = "md",
  variant = "auto",
  withTagline,
  className,
  glow,
}: LockupProps) {
  const s = SIZE_MAP[size];
  return (
    <div className={clsx("flex items-center gap-3", className)}>
      <BrandSymbol className={s.sym} glow={glow} />
      <div className="flex flex-col leading-none">
        <BrandWordmark className={s.wm} variant={variant} />
        {withTagline && (
          <span
            className={clsx(
              "mt-1 font-mono text-[8.5px] font-medium uppercase",
              variant === "light" ? "text-slate-300" : "text-slate-500 dark:text-slate-400",
              size === "lg" && "whitespace-nowrap",
            )}
            style={{ letterSpacing: "0.06em" }}
          >
            Monitoramento inteligente
          </span>
        )}
      </div>
    </div>
  );
}
